import { MemberReceipts } from './MemberReceipts';
import { memberPlanPresets } from './memberPlanPresets';
import { useSearchParams } from 'react-router-dom';
import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, useData, post, useSession } from '@yingbaba/api';
import {
  Button,
  Empty,
  Field,
  Modal,
  PageTitle,
  ErrorNotice,
  Notice,
  Select,
  Status,
  ButtonLink,
} from '@yingbaba/ui';
import {
  beijingTimestamp,
  datetime,
  money,
  parseMemberMoney,
  memberBenefitText,
  memberStatus,
  memberCommandKey,
  clearMemberCommand,
} from '@yingbaba/utils';
import type {
  Page,
  PaidMemberBenefitView,
  PaidMemberOrderView,
  PaidMemberPlanView,
  PaidMemberPrice,
  PaidMemberSubscription,
} from '@yingbaba/types';

type Dialog =
  | { kind: 'plan'; plan?: PaidMemberPlanView; preset?: (typeof memberPlanPresets)[number] }
  | { kind: 'price' | 'benefits'; plan: PaidMemberPlanView }
  | { kind: 'retire'; price: PaidMemberPrice }
  | { kind: 'confirm'; order: PaidMemberOrderView }
  | { kind: 'revoke'; subscription: PaidMemberSubscription }
  | { kind: 'grant' };
const titles = {
  plan: '会员等级设置',
  price: '发布会员价格',
  benefits: '编辑套餐权益',
  retire: '下架会员价格',
  confirm: '核实会员到账',
  revoke: '撤销会员权益',
  grant: '赠送会员',
};
const inputStyle = { display: 'grid', gap: 14 };

export function MemberManagement() {
  const session = useSession();
  const role = session.data?.account.platform_role || '';
  const admin = role === 'super_admin';
  const finance = admin || role === 'finance';
  const readable = finance || role === 'auditor';
  const qc = useQueryClient();
  const [urlParams, setURLParams] = useSearchParams();
  const requestedTab = urlParams.get('tab');
  const tab =
    requestedTab && ['plans', 'orders', 'receipts', 'subscriptions'].includes(requestedTab)
      ? requestedTab
      : window.location.hash === '#member-receipts' ||
          urlParams.has('receipt_order') ||
          role === 'finance'
        ? 'receipts'
        : 'plans';
  function updateURL(values: Record<string, string>) {
    const next = new URLSearchParams(urlParams);
    for (const [key, value] of Object.entries(values)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setURLParams(next);
  }
  const orderPage = Math.min(
    10000,
    Math.max(1, Math.floor(Number(urlParams.get('order_page')) || 1)),
  );
  const subPage = Math.min(10000, Math.max(1, Math.floor(Number(urlParams.get('sub_page')) || 1)));
  const filter = ['pending', 'expired', 'paid', 'cancelled', 'payment_review', 'refunded'].includes(
    urlParams.get('order_status') || '',
  )
    ? urlParams.get('order_status')!
    : '';
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const plans = useData<PaidMemberPlanView[]>(
    '/admin/member/plans',
    readable && (tab === 'plans' || dialog?.kind === 'grant'),
  );
  const orders = useData<Page<PaidMemberOrderView>>(
    `/admin/member/orders?page=${orderPage}${filter ? `&status=${filter}` : ''}`,
    readable && tab === 'orders',
    30_000,
  );
  const subs = useData<Page<PaidMemberSubscription>>(
    `/admin/member/subscriptions?page=${subPage}`,
    readable && tab === 'subscriptions',
    30_000,
  );
  const [benefits, setBenefits] = useState<PaidMemberBenefitView[]>([]);
  const [grantType, setGrantType] = useState('account');
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const priceInput = useRef<HTMLInputElement>(null);
  const [priceError, setPriceError] = useState('');
  function readPrice(value: string): number | null {
    try {
      const amount = parseMemberMoney(value);
      setPriceError('');
      return amount;
    } catch (error) {
      setPriceError(error instanceof Error ? error.message : '请输入有效的售价。');
      return null;
    }
  }
  function open(next: Dialog) {
    setError(null);
    setPriceError('');
    setDialog(next);
    setGrantType('account');
    if (next.kind === 'benefits') setBenefits(next.plan.benefits.map((b) => ({ ...b })));
  }
  function updateBenefit(index: number, patch: Partial<PaidMemberBenefitView>) {
    setBenefits((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }
  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!dialog || pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    const f = new FormData(e.currentTarget);
    const text = (name: string) => String(f.get(name) || '').trim();
    let commandScope = '';
    try {
      switch (dialog.kind) {
        case 'plan': {
          const p = dialog.plan;
          const data = {
            code: p?.code || text('code'),
            name: text('name'),
            audience: p?.audience || text('audience'),
            description: text('description'),
            level: Number(text('level')),
            display_order: Number(text('display_order')),
            status: text('status'),
            ...(p ? { expected_revision: p.revision } : {}),
          };
          if (p)
            await api(`/admin/member/plans/${p.id}`, { method: 'PUT', body: JSON.stringify(data) });
          else await post('/admin/member/plans', data);
          break;
        }
        case 'price': {
          const amount = readPrice(text('amount'));
          if (amount === null) {
            priceInput.current?.focus();
            return;
          }
          await post(`/admin/member/plans/${dialog.plan.id}/prices`, {
            duration_months: Number(text('months')),
            amount_minor: amount,
          });
          break;
        }
        case 'retire':
          await post(`/admin/member/prices/${dialog.price.id}/retire`);
          break;
        case 'benefits':
          await api(`/admin/member/plans/${dialog.plan.id}/benefits`, {
            method: 'PUT',
            body: JSON.stringify({ expected_revision: dialog.plan.revision, items: benefits }),
          });
          break;
        case 'confirm':
          await post(`/admin/member/orders/${dialog.order.id}/confirm-manual`, {
            reference: text('reference'),
            amount_minor: parseMemberMoney(text('amount')),
            currency: 'CNY',
            received_at: beijingTimestamp(text('received_at')),
            reason: text('reason'),
          });
          break;
        case 'grant': {
          const data = {
            owner_type: grantType,
            owner_id: text('owner_id'),
            plan_id: text('plan_id'),
            duration_months: Number(text('months')),
            reason: text('reason'),
          };
          commandScope = `${session.data?.account.id}:grant`;
          await post('/admin/member/grants', {
            ...data,
            idempotency_key: await memberCommandKey(commandScope, data),
          });
          break;
        }
        case 'revoke': {
          const data = { reason: text('reason'), expected_version: dialog.subscription.version };
          commandScope = `${session.data?.account.id}:revoke:${dialog.subscription.id}`;
          await post(`/admin/member/subscriptions/${dialog.subscription.id}/revoke`, {
            ...data,
            idempotency_key: await memberCommandKey(commandScope, data),
          });
          break;
        }
      }
      if (commandScope) clearMemberCommand(commandScope);
      setDialog(null);
      setNotice('操作已保存并写入审计。');
      await qc.invalidateQueries({
        predicate: (q) =>
          typeof q.queryKey[0] === 'string' && q.queryKey[0].startsWith('/admin/member/'),
      });
    } catch (e) {
      setError(e);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  if (!readable) return <Empty title="当前角色无会员管理权限" />;
  return (
    <div className="admin-member-page">
      <PageTitle
        title="付费会员管理"
        description="管理会员等级、购买订单、到账核查与服务周期。"
        action={
          tab === 'plans' && admin ? (
            <Button onClick={() => open({ kind: 'plan' })}>新增会员等级</Button>
          ) : undefined
        }
      />
      {notice && <Notice>{notice}</Notice>}
      {!dialog && <ErrorNotice error={error} />}
      <nav className="tabs admin-section-tabs" aria-label="会员管理分类">
        {(
          [
            ['plans', '等级与价格'],
            ['orders', '会员订单'],
            ['receipts', '收款与退款'],
            ['subscriptions', '会员订阅'],
          ] as const
        ).map(([key, name]) => (
          <button
            key={key}
            className={tab === key ? 'active' : ''}
            aria-current={tab === key ? 'page' : undefined}
            onClick={() => updateURL({ tab: key })}
          >
            {name}
          </button>
        ))}
      </nav>
      {tab === 'plans' && (
        <>
          <Notice>
            当前等级和权益模板用于运营配置，尚未接入
            AI、存储等业务权益。请确认可交付服务、价格及退款说明后再上架销售。
          </Notice>
          <div className="admin-section-heading">
            <div>
              <h2>等级与价格</h2>
              <p className="muted">个人与组织分别配置；新价格不会修改已有订单。</p>
            </div>
            <ButtonLink to="/payment-settings?business=member" variant="secondary">
              支付方式
            </ButtonLink>
          </div>
          <ErrorNotice error={plans.error} />
          {plans.isLoading ? (
            <p>正在加载配置…</p>
          ) : (
            plans.isSuccess &&
            (plans.data.length ? (
              <div className="admin-member-plan-grid">
                {plans.data.map((p) => (
                  <section className="card admin-member-plan" key={p.id}>
                    <div className="admin-record-heading">
                      <h3>{p.name}</h3>
                      <Status
                        value={p.status}
                        label={
                          p.status === 'active'
                            ? '已上架'
                            : p.status === 'draft'
                              ? '草稿'
                              : '已归档'
                        }
                      />
                    </div>
                    <p className="muted small">
                      {p.audience === 'account' ? '个人会员' : '组织会员'} · 等级 {p.level} ·{' '}
                      {p.code}
                    </p>
                    <p className="admin-plan-description">{p.description || '尚未填写等级说明'}</p>
                    {!!p.benefits.length && (
                      <details className="admin-plan-benefits">
                        <summary>查看权益模板（{p.benefits.length} 项）</summary>
                        {p.benefits.map((b) => (
                          <p key={b.code}>{memberBenefitText(b)}</p>
                        ))}
                      </details>
                    )}
                    <div className="admin-plan-prices">
                      {p.prices
                        .filter((price) => price.status === 'active')
                        .map((price) => (
                          <div
                            className="row"
                            style={{ flexWrap: 'wrap', gap: 12, marginTop: 12 }}
                            key={price.id}
                          >
                            <span>
                              {price.duration_months} 个月 · {money(price.amount_minor)} · v
                              {price.version}
                              {price.status === 'retired' ? ' · 已下架' : ''}
                            </span>
                            {admin && price.status === 'active' && (
                              <Button
                                variant="secondary"
                                onClick={() => open({ kind: 'retire', price })}
                              >
                                下架价格
                              </Button>
                            )}
                          </div>
                        ))}
                      {!p.prices.some((price) => price.status === 'active') && (
                        <p className="muted">待设置售价 · 暂不可购买</p>
                      )}
                    </div>
                    {p.prices.some((price) => price.status === 'retired') && (
                      <details className="admin-plan-benefits">
                        <summary>历史价格</summary>
                        {p.prices
                          .filter((price) => price.status === 'retired')
                          .map((price) => (
                            <p key={price.id}>
                              {price.duration_months} 个月 · {money(price.amount_minor)} · v
                              {price.version} · 已下架
                            </p>
                          ))}
                      </details>
                    )}
                    {admin && (
                      <div className="row" style={{ flexWrap: 'wrap', gap: 10, marginTop: 16 }}>
                        <Button variant="secondary" onClick={() => open({ kind: 'plan', plan: p })}>
                          编辑等级
                        </Button>
                        {p.status !== 'archived' && (
                          <>
                            <Button
                              variant="secondary"
                              onClick={() => open({ kind: 'price', plan: p })}
                            >
                              发布价格
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={() => open({ kind: 'benefits', plan: p })}
                            >
                              编辑权益
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </section>
                ))}
              </div>
            ) : (
              <Empty
                title="尚未配置会员等级"
                description="可从推荐模板创建个人或组织等级，补齐可交付权益和价格后再上架。"
              />
            ))
          )}
        </>
      )}
      {tab === 'orders' && (
        <section className="admin-data-panel">
          <div className="admin-section-heading">
            <div>
              <h2>会员订单</h2>
              <p className="muted">线上订单以渠道核验为准；人工收款请核对真实流水。</p>
            </div>
          </div>
          <div className="admin-data-toolbar">
            <Field label="会员订单状态">
              <Select
                value={filter}
                onChange={(v) => {
                  updateURL({ order_status: v, order_page: '' });
                }}
              >
                <option value="">全部状态</option>
                <option value="pending">待核款</option>
                <option value="expired">已过期申请</option>
                <option value="paid">已核款</option>
                <option value="cancelled">已取消</option>
                <option value="payment_review">已到账待核查</option>
                <option value="refunded">已退款</option>
              </Select>
            </Field>
          </div>
          <ErrorNotice error={orders.error} />
          {orders.isLoading ? (
            <p>正在加载订单…</p>
          ) : (
            orders.isSuccess &&
            (orders.data.items.length ? (
              orders.data.items.map((o) => (
                <div className="list-item admin-record" key={o.id}>
                  <div className="grow" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    <strong>
                      {o.plan_name} · {money(o.amount_minor)}
                    </strong>
                    <p>
                      {memberStatus[o.status]} · {o.owner_type === 'account' ? '个人' : '组织'} /{' '}
                      {o.owner_id}
                    </p>
                    <p className="small muted">
                      订单：{o.id} · 有效至 {datetime(o.expires_at)}
                    </p>
                    <details>
                      <summary>订单权益及付款快照</summary>
                      {o.benefits.map((b) => (
                        <p key={b.code}>{memberBenefitText(b)}</p>
                      ))}
                      {o.pay_reference && <p>到账流水：{o.pay_reference}</p>}
                      {o.paid_at && <p>到账时间：{datetime(o.paid_at)}</p>}
                    </details>
                  </div>
                  {finance &&
                    o.collection_mode !== 'online' &&
                    ['pending', 'expired'].includes(o.status) && (
                      <Button onClick={() => open({ kind: 'confirm', order: o })}>
                        {o.status === 'expired' ? '核查过期申请到账' : '核实到账'}
                      </Button>
                    )}
                </div>
              ))
            ) : (
              <Empty title="暂无该状态的会员订单" />
            ))
          )}
          <div className="admin-pagination">
            <Button
              variant="secondary"
              disabled={orderPage <= 1}
              onClick={() => updateURL({ order_page: String(orderPage - 1) })}
            >
              上一页会员订单
            </Button>
            <span>
              第 {orderPage} 页 / 共 {orders.data?.total || 0} 条
            </span>
            <Button
              variant="secondary"
              disabled={!orders.data || orderPage * orders.data.page_size >= orders.data.total}
              onClick={() => updateURL({ order_page: String(orderPage + 1) })}
            >
              下一页会员订单
            </Button>
          </div>
        </section>
      )}
      {tab === 'subscriptions' && (
        <section className="admin-data-panel">
          <div className="admin-section-heading">
            <h2>会员订阅</h2>
            {admin && (
              <Button variant="secondary" onClick={() => open({ kind: 'grant' })}>
                赠送会员
              </Button>
            )}
          </div>
          <ErrorNotice error={subs.error} />
          {subs.isLoading ? (
            <p>正在加载订阅…</p>
          ) : (
            subs.isSuccess &&
            (subs.data.items.length ? (
              subs.data.items.map((s) => (
                <div className="list-item admin-record" key={s.id}>
                  <div className="grow" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    <strong>{s.plan_name}</strong>
                    <p>
                      {s.owner_type === 'account' ? '个人' : '组织'} / {s.owner_id}
                    </p>
                    <p>
                      {memberStatus[s.status]} · 服务到期时间 {datetime(s.expires_at)} · 版本{' '}
                      {s.version}
                    </p>
                  </div>
                  {admin && ['active', 'scheduled', 'suspended'].includes(s.status) && (
                    <Button
                      variant="secondary"
                      onClick={() => open({ kind: 'revoke', subscription: s })}
                    >
                      撤销权益
                    </Button>
                  )}
                </div>
              ))
            ) : (
              <Empty title="暂无会员订阅" />
            ))
          )}
          <div className="admin-pagination">
            <Button
              variant="secondary"
              disabled={subPage <= 1}
              onClick={() => updateURL({ sub_page: String(subPage - 1) })}
            >
              上一页订阅
            </Button>
            <span>
              第 {subPage} 页 / 共 {subs.data?.total || 0} 条
            </span>
            <Button
              variant="secondary"
              disabled={!subs.data || subPage * subs.data.page_size >= subs.data.total}
              onClick={() => updateURL({ sub_page: String(subPage + 1) })}
            >
              下一页订阅
            </Button>
          </div>
        </section>
      )}
      {tab === 'receipts' && <MemberReceipts />}
      {dialog && (
        <Modal
          open
          busy={busy}
          title={titles[dialog.kind]}
          onOpenChange={(open) => {
            if (!open && !busy) setDialog(null);
          }}
          footer={
            <Button
              type="submit"
              form="admin-member-dialog"
              variant={['retire', 'revoke'].includes(dialog.kind) ? 'danger' : 'primary'}
              loading={busy}
            >
              {
                {
                  plan: '保存等级',
                  price: '发布价格',
                  benefits: '保存权益模板',
                  retire: '确认下架价格',
                  confirm: '确认核实到账',
                  grant: '确认赠送会员',
                  revoke: '确认撤销权益',
                }[dialog.kind]
              }
            </Button>
          }
        >
          <form
            id="admin-member-dialog"
            key={
              dialog.kind === 'plan'
                ? dialog.plan?.id || dialog.preset?.code || 'custom'
                : dialog.kind
            }
            onSubmit={submit}
            style={inputStyle}
          >
            <ErrorNotice error={error} />
            {dialog.kind === 'plan' && (
              <>
                {!dialog.plan && (
                  <Field label="起始模板" hint="模板只预填等级资料，不包含售价或已兑现的业务权益。">
                    <Select
                      aria-label="起始模板"
                      value={dialog.preset?.code || ''}
                      onChange={(code) =>
                        setDialog({
                          kind: 'plan',
                          preset: memberPlanPresets.find((p) => p.code === code),
                        })
                      }
                    >
                      <option value="">自定义等级</option>
                      {memberPlanPresets.map((p) => (
                        <option key={p.code} value={p.code}>
                          {p.name} · {p.audience === 'account' ? '个人' : '组织'}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
                <Field label="等级代码">
                  <input
                    className="input"
                    aria-label="等级代码"
                    name="code"
                    required
                    pattern="[a-z][a-z0-9_]{1,39}"
                    maxLength={40}
                    defaultValue={(dialog.plan || dialog.preset)?.code}
                    readOnly={!!dialog.plan}
                  />
                </Field>
                <Field label="等级名称">
                  <input
                    className="input"
                    aria-label="等级名称"
                    name="name"
                    required
                    maxLength={80}
                    defaultValue={(dialog.plan || dialog.preset)?.name}
                  />
                </Field>
                <Field label="说明">
                  <textarea
                    aria-label="说明"
                    name="description"
                    maxLength={500}
                    defaultValue={(dialog.plan || dialog.preset)?.description}
                  />
                </Field>
                <Field label="适用主体">
                  <Select
                    name="audience"
                    defaultValue={(dialog.plan || dialog.preset)?.audience || 'account'}
                    disabled={!!dialog.plan}
                  >
                    <option value="account">个人</option>
                    <option value="organization">组织</option>
                  </Select>
                </Field>
                <Field label="等级序号">
                  <input
                    className="input"
                    aria-label="等级序号"
                    name="level"
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    required
                    defaultValue={(dialog.plan || dialog.preset)?.level || 1}
                  />
                </Field>
                <Field label="展示顺序">
                  <input
                    className="input"
                    aria-label="展示顺序"
                    name="display_order"
                    type="number"
                    min="0"
                    max="10000"
                    step="1"
                    required
                    defaultValue={(dialog.plan || dialog.preset)?.display_order || 0}
                  />
                </Field>
                <Field label="销售状态">
                  <Select name="status" defaultValue={dialog.plan?.status || 'draft'}>
                    <option value="draft">草稿 / 暂停售卖</option>
                    <option value="active">上架</option>
                    <option value="archived">归档</option>
                  </Select>
                </Field>
              </>
            )}
            {dialog.kind === 'price' && (
              <>
                <p>{dialog.plan.name}。发布新价格后，同周期旧价格下架，已创建订单不变。</p>
                <Field label="购买周期">
                  <Select name="months" defaultValue="12">
                    <option value="1">1 个月</option>
                    <option value="3">3 个月</option>
                    <option value="12">12 个月</option>
                  </Select>
                </Field>
                <Field label="售价（元）" htmlFor="member-price-amount">
                  <div
                    className="member-price-control"
                    data-invalid={priceError ? 'true' : undefined}
                  >
                    <span className="member-price-currency" aria-hidden="true">
                      ¥
                    </span>
                    <input
                      ref={priceInput}
                      id="member-price-amount"
                      className="input"
                      aria-label="售价（元）"
                      aria-invalid={!!priceError}
                      aria-describedby={`member-price-help${priceError ? ' member-price-error' : ''}`}
                      name="amount"
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      maxLength={10}
                      required
                      placeholder="0.00"
                      onChange={() => setPriceError('')}
                      onBlur={(event) => {
                        if (event.target.value.trim()) readPrice(event.target.value);
                      }}
                    />
                    <span className="member-price-unit" aria-hidden="true">
                      元
                    </span>
                  </div>
                  <small id="member-price-help">
                    人民币，最多两位小数；范围 0.01–1,000,000 元。
                  </small>
                  {priceError && (
                    <span id="member-price-error" className="field-error" role="alert">
                      {priceError}
                    </span>
                  )}
                </Field>
              </>
            )}
            {dialog.kind === 'retire' && (
              <Notice>
                下架 {dialog.price.duration_months} 个月 / {money(dialog.price.amount_minor)}{' '}
                的价格版本。既有订单仍按原快照核款。
              </Notice>
            )}
            {dialog.kind === 'confirm' && (
              <>
                <p>
                  {dialog.order.plan_name} · 应收 {money(dialog.order.amount_minor)} · 订单{' '}
                  {dialog.order.id}
                </p>
                <Notice>
                  请独立核对银行或支付渠道流水。过期申请仅可确认有效期内实际到账的款项；逾期到账、金额不符或归属停用必须人工处理，不会自动开通。
                </Notice>
                <Field label="真实到账流水编号">
                  <input
                    className="input"
                    aria-label="真实到账流水编号"
                    name="reference"
                    required
                    minLength={6}
                    maxLength={120}
                  />
                </Field>
                <Field label="实收金额（人民币元）">
                  <input
                    className="input"
                    aria-label="实收金额（人民币元）"
                    name="amount"
                    inputMode="decimal"
                    required
                  />
                </Field>
                <Field label="到账时间（北京时间）">
                  <input
                    className="input"
                    aria-label="到账时间（北京时间）"
                    name="received_at"
                    type="datetime-local"
                    step="1"
                    required
                  />
                </Field>
              </>
            )}
            {dialog.kind === 'revoke' && (
              <Notice>
                撤销 {dialog.subscription.plan_name}{' '}
                当前及未来服务周期。原付款、原服务到期时间和审计记录保留，不代表已退款。若订阅已发生续费，本次操作将被拒绝。
              </Notice>
            )}
            {dialog.kind === 'grant' && (
              <>
                <Field label="归属类型">
                  <Select value={grantType} onChange={setGrantType}>
                    <option value="account">个人</option>
                    <option value="organization">组织</option>
                  </Select>
                </Field>
                <Field label="归属 ID">
                  <input
                    className="input"
                    aria-label="归属 ID"
                    name="owner_id"
                    required
                    pattern="[0-9A-HJKMNP-TV-Z]{26}"
                  />
                </Field>
                <Field label="会员等级">
                  <Select key={grantType} name="plan_id" required defaultValue="">
                    <option value="">请选择</option>
                    {plans.data
                      ?.filter((p) => p.status === 'active' && p.audience === grantType)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                  </Select>
                </Field>
                <Field label="赠送月数">
                  <input
                    className="input"
                    aria-label="赠送月数"
                    name="months"
                    type="number"
                    min="1"
                    max="36"
                    step="1"
                    required
                    defaultValue="1"
                  />
                </Field>
              </>
            )}
            {['confirm', 'grant', 'revoke'].includes(dialog.kind) && (
              <Field label="处理原因与核验依据">
                <textarea
                  aria-label="处理原因与核验依据"
                  name="reason"
                  required
                  minLength={6}
                  maxLength={500}
                />
              </Field>
            )}
            {dialog.kind === 'benefits' && (
              <>
                <Notice>
                  只修改套餐模板，不改写已有订单或服务周期。额度和重置周期为配置，不表示已执行消费计数或业务接入。
                </Notice>
                {benefits.map((b, i) => (
                  <fieldset key={i} style={{ display: 'grid', gap: 10, minWidth: 0 }}>
                    <legend>权益 {i + 1}</legend>
                    <Field label="权益代码">
                      <input
                        className="input"
                        aria-label="权益代码"
                        required
                        pattern="[a-z][a-z0-9_]{1,63}"
                        value={b.code}
                        onChange={(e) => updateBenefit(i, { code: e.target.value })}
                      />
                    </Field>
                    <Field label="权益名称">
                      <input
                        className="input"
                        aria-label="权益名称"
                        required
                        maxLength={100}
                        value={b.label}
                        onChange={(e) => updateBenefit(i, { label: e.target.value })}
                      />
                    </Field>
                    <Field label="数值类型">
                      <Select
                        value={b.value_type}
                        onChange={(v) =>
                          updateBenefit(i, {
                            value_type: v as PaidMemberBenefitView['value_type'],
                            value: v === 'boolean' ? false : 0,
                            reset_period: 'none',
                          })
                        }
                      >
                        <option value="boolean">是否包含</option>
                        <option value="quota">额度</option>
                        <option value="discount_bps">优惠基点</option>
                      </Select>
                    </Field>
                    <Field label="权益值">
                      {b.value_type === 'boolean' ? (
                        <Select
                          value={String(b.value)}
                          onChange={(v) => updateBenefit(i, { value: v === 'true' })}
                        >
                          <option value="true">包含</option>
                          <option value="false">不包含</option>
                        </Select>
                      ) : (
                        <input
                          className="input"
                          aria-label="权益值"
                          type="number"
                          min="0"
                          max={b.value_type === 'discount_bps' ? 10000 : 100000000}
                          step="1"
                          required
                          value={Number(b.value)}
                          onChange={(e) => updateBenefit(i, { value: e.target.valueAsNumber })}
                        />
                      )}
                    </Field>
                    {b.value_type === 'quota' && (
                      <>
                        <Field label="单位">
                          <input
                            className="input"
                            aria-label="单位"
                            maxLength={32}
                            value={b.unit}
                            onChange={(e) => updateBenefit(i, { unit: e.target.value })}
                          />
                        </Field>
                        <Field label="重置周期">
                          <Select
                            value={b.reset_period}
                            onChange={(v) =>
                              updateBenefit(i, {
                                reset_period: v as PaidMemberBenefitView['reset_period'],
                              })
                            }
                          >
                            <option value="none">不重置</option>
                            <option value="day">按日</option>
                            <option value="month">按月</option>
                          </Select>
                        </Field>
                      </>
                    )}
                    {b.value_type === 'discount_bps' && (
                      <p className="small muted">
                        1000 表示优惠 10%，不是实际交易价格；尚未关联佣金计算。
                      </p>
                    )}
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setBenefits((rows) => rows.filter((_, n) => n !== i))}
                    >
                      移除此权益
                    </Button>
                  </fieldset>
                ))}
                <Button
                  type="button"
                  variant="secondary"
                  disabled={benefits.length >= 50}
                  onClick={() =>
                    setBenefits((rows) => [
                      ...rows,
                      {
                        code: '',
                        label: '',
                        value_type: 'boolean',
                        value: false,
                        unit: '',
                        reset_period: 'none',
                      },
                    ])
                  }
                >
                  添加权益
                </Button>
              </>
            )}
          </form>
        </Modal>
      )}
    </div>
  );
}
